import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { ArrowRight, Calendar, Cpu, MapPin, Users, ClipboardList } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidatesList,
  useGroupsList,
  useAllSessions,
  useEffectiveUser,
  useUsersByRole,
  useExpedientesForEvaluator,
} from "@/store/hooks";

type EstadoFiltro = "todos" | "pendiente-prechecklist" | "pendiente-practica" | "lista-dictamen" | "completada";

function estadoExpediente(exp: { evaluacionPractica: { aplicada: boolean }; dictamen: string }, hasPrechecklist: boolean): EstadoFiltro {
  if (exp.dictamen !== "pendiente") return "completada";
  if (exp.evaluacionPractica.aplicada) return "lista-dictamen";
  if (hasPrechecklist) return "pendiente-practica";
  return "pendiente-prechecklist";
}

const estadoLabel: Record<EstadoFiltro, string> = {
  "todos": "Todos los estados",
  "pendiente-prechecklist": "Pendiente prechecklist",
  "pendiente-practica": "Pendiente práctica",
  "lista-dictamen": "Listo para dictamen",
  "completada": "Dictaminado",
};
const estadoTone: Record<EstadoFiltro, "amber" | "teal" | "green" | "gray"> = {
  "todos": "gray",
  "pendiente-prechecklist": "amber",
  "pendiente-practica": "amber",
  "lista-dictamen": "teal",
  "completada": "green",
};

export default function EvaluatorDashboard() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("evaluador"), [setRole]);

  const candidates = useCandidatesList();
  const groups = useGroupsList();
  const allSessions = useAllSessions();
  const me = useEffectiveUser();
  const allEvaluators = useUsersByRole("evaluador");
  const fallbackEvaluator = allEvaluators[0];
  const effectiveEvalId = (me?.roles.includes("evaluador") ? me.id : fallbackEvaluator?.id) ?? "";
  const prechecklistByExp = useDemoStore((s) => s.prechecklistByExpediente);

  const myAssignments = useExpedientesForEvaluator(effectiveEvalId);

  const myGroups = useMemo(
    () => groups.filter((g) => (g.evaluadoresIds ?? [g.evaluadorId]).includes(effectiveEvalId)),
    [groups, effectiveEvalId],
  );

  const groupOptions = useMemo(() => [{ id: "todos", nombre: "Todos los grupos" }, ...myGroups.map((g) => ({ id: g.id, nombre: g.nombre }))], [myGroups]);
  const standardOptions = useMemo(() => {
    const codes = Array.from(new Set(myAssignments.map((a) => a.exp.estandar)));
    return [{ codigo: "todos", titulo: "Todos los estándares" }, ...codes.map((c) => ({ codigo: c, titulo: c }))];
  }, [myAssignments]);

  const [filtroGrupo, setFiltroGrupo] = useState("todos");
  const [filtroStd, setFiltroStd] = useState("todos");
  const [filtroEstado, setFiltroEstado] = useState<EstadoFiltro>("todos");

  const filtered = useMemo(() => {
    return myAssignments.filter(({ exp, grupoId }) => {
      if (filtroGrupo !== "todos" && grupoId !== filtroGrupo) return false;
      if (filtroStd !== "todos" && exp.estandar !== filtroStd) return false;
      const hasPre = !!prechecklistByExp[exp.id]?.completedAt;
      const est = estadoExpediente(exp, hasPre);
      if (filtroEstado !== "todos" && est !== filtroEstado) return false;
      return true;
    });
  }, [myAssignments, filtroGrupo, filtroStd, filtroEstado, prechecklistByExp]);

  const today = new Date().toISOString().slice(0, 10);
  const mySessionsToday = useMemo(
    () => allSessions.filter((s) => s.evaluadorId === effectiveEvalId && s.fecha === today),
    [allSessions, effectiveEvalId, today],
  );
  const groupForLink = myGroups[0] ?? groups.find((g) => g.estado === "en-curso") ?? groups[0];

  return (
    <AppLayout pageTitle="Agenda del evaluador">
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 16 }}>
        <Card>
          <SectionTitle action={
            <Link href="/evaluador/historial" style={{ fontSize: 12, color: "var(--cc-teal)", fontWeight: 600, textDecoration: "none" }} data-testid="link-history">
              Mis evaluaciones →
            </Link>
          }>
            Mi bandeja {myGroups.length > 0 ? `· ${myGroups.length} grupos · ${myAssignments.length} candidatos` : ""}
          </SectionTitle>

          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <select value={filtroGrupo} onChange={(e) => setFiltroGrupo(e.target.value)} data-testid="filter-group" style={selectStyle}>
              {groupOptions.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
            <select value={filtroStd} onChange={(e) => setFiltroStd(e.target.value)} data-testid="filter-standard" style={selectStyle}>
              {standardOptions.map((s) => <option key={s.codigo} value={s.codigo}>{s.titulo}</option>)}
            </select>
            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value as EstadoFiltro)} data-testid="filter-estado" style={selectStyle}>
              {(Object.keys(estadoLabel) as EstadoFiltro[]).map((k) => <option key={k} value={k}>{estadoLabel[k]}</option>)}
            </select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--cc-gray-500)", padding: 16, textAlign: "center" }}>
                Sin expedientes que coincidan con los filtros.
              </div>
            )}
            {filtered.map(({ exp, grupoId }) => {
              const cand = candidates.find((c) => c.id === exp.candidatoId);
              if (!cand) return null;
              const hasPre = !!prechecklistByExp[exp.id]?.completedAt;
              const est = estadoExpediente(exp, hasPre);
              const grp = groups.find((g) => g.id === grupoId);
              return (
                <Link
                  key={exp.id}
                  href={`/evaluador/evaluacion/${exp.id}/prechecklist`}
                  data-testid={`row-eval-${exp.id}`}
                  style={{ textDecoration: "none" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, border: "1px solid var(--cc-gray-100)", borderRadius: 10, cursor: "pointer" }}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", background: cand.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{cand.iniciales}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{cand.nombre}</div>
                      <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{exp.estandar} · {grp?.nombre ?? "—"} · {cand.matricula}</div>
                    </div>
                    <Badge tone={estadoTone[est]}>{estadoLabel[est]}</Badge>
                    <ArrowRight size={16} color="var(--cc-gray-400)" />
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle>Mi jornada</SectionTitle>
            {mySessionsToday.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)", padding: 6 }}>Sin sesiones para hoy.</div>
            )}
            {mySessionsToday.map((s) => (
              <Row key={s.id} icon={Calendar} text={`${s.titulo} · ${s.hora} (${s.duracionMin} min)`} />
            ))}
            <Row icon={MapPin} text={groupForLink?.sede ?? "Sin sede asignada"} />
            <Row icon={Users} text={`${myAssignments.length} candidatos asignados`} />
            <Row icon={ClipboardList} text={`${filtered.length} expedientes en vista`} />
          </Card>

          <Link href="/evaluador/ia" data-testid="link-quick-ia" style={{ textDecoration: "none" }}>
            <Card style={{ cursor: "pointer", background: "var(--cc-teal-bg)", border: "1px solid #a5f3fc" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <Cpu size={15} color="var(--cc-teal)" />
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Pregúntale a CertiAI</span>
                <ArrowRight size={13} color="var(--cc-teal)" style={{ marginLeft: "auto" }} />
              </div>
              <div style={{ fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.6 }}>
                Criterios críticos, recordatorio de prechecklist y apoyo para redactar observaciones consistentes.
              </div>
            </Card>
          </Link>

          {filtered[0] && (
            <Link href={`/evaluador/evaluacion/${filtered[0].exp.id}/prechecklist`} data-testid="link-start-eval" style={{ display: "block" }}>
              <PrimaryButton>Iniciar lista preoperativa <ArrowRight size={13} /></PrimaryButton>
            </Link>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

const selectStyle: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid var(--cc-gray-200)",
  borderRadius: 8,
  fontSize: 12,
  background: "white",
  color: "var(--cc-gray-800)",
};

function Row({ icon: Icon, text }: { icon: React.ComponentType<{ size?: number; color?: string }>; text: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid var(--cc-gray-100)", fontSize: 13, color: "var(--cc-gray-700)" }}>
      <Icon size={14} color="var(--cc-gray-400)" /> {text}
    </div>
  );
}
